import { getExtraTimeMs } from '@/lib/accommodationTime';
import { convertMs, extraTimeValues } from '@/lib/commonHelpers';
import { resolveAccommodations } from '@/pages/experiences/utils/realtimeExperienceHelper';

// Hard cap for manually-typed extra time. Above this we don't surface a custom
// option in the dropdown — prevents accidents like typing "999" minutes.
export const MAX_CUSTOM_MINUTES = 120;

// Subset of the realtime student record that the extra-time helpers actually
// read. Kept narrow so the helpers don't depend on the full monitor item shape.
export interface StudentRealtimeData {
  extra_time?: number; // ms of extra time already applied
  pending_extra_time?: number; // ms of extra time granted but not yet applied (offline-pending)
  extra_time_offset?: number; // ms of non-pause dead time after the original timer expired
  currentView?: string;
  started_at?: number | null; // epoch ms when the student started the test
  is_time_up?: boolean;
  pause_status?: string | null;
  unpaused_offset?: number; // ms of accumulated pause time (set by pause/unpause handlers)
  reset_submission_at?: number; // epoch ms of the last teacher reset
}

// Strip non-digits, cap length, and drop leading zeros so "020" normalizes to
// "20" — otherwise it shows as a custom option alongside the "20" preset.
// 3 digits is enough for our 120-minute cap.
export const sanitizeMinutesInput = (val: string): string =>
  val
    .replace(/[^0-9]/g, '')
    .slice(0, 3)
    .replace(/^0+(?=\d)/, '');

// Format ms as "X minutes Y seconds" (units pluralized, zero parts omitted) for
// audit logs and student-facing notifications. Used so percentage additions
// that resolve to fractional minutes (e.g. 10% of 1m = 6s) still surface a
// meaningful duration instead of being floored to "0 minutes".
export const formatExtraDuration = (ms: number): string => {
  const totalSeconds = Math.max(0, Math.round(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  const parts: string[] = [];
  if (minutes > 0) parts.push(`${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`);
  if (seconds > 0) parts.push(`${seconds} ${seconds === 1 ? 'second' : 'seconds'}`);
  return parts.join(' ') || '0 seconds';
};

// True when the typed value should appear as a "custom" option above the presets:
// numeric, in (0, 120], and not already a preset (presets are stored as strings,
// so we compare strings to avoid "10" vs 10 mismatches).
export const isCustomMinutesValue = (searchValue: string): boolean => {
  const num = Number(searchValue);
  return num > 0 && num <= MAX_CUSTOM_MINUTES && !extraTimeValues.includes(searchValue);
};

export const PERCENTAGE_PRESETS = ['10', '25', '50', '75', '100'];

// Custom percentage: any integer in (0, 100] that isn't already a preset.
// Mirrors isCustomMinutesValue but with the percent cap.
// FE caps to 1-100 for UX; BE is the authority on accommodation bounds.
export const isCustomPercentageValue = (searchValue: string): boolean => {
  const num = Number(searchValue);
  return num > 0 && num <= 100 && !PERCENTAGE_PRESETS.includes(searchValue);
};

// Prefix-match for antd Select.filterOption. option.value is string | number,
// so we coerce to string before calling .startsWith.
export const filterOptionByPrefix = (
  input: string,
  option?: { value?: string | number }
): boolean => String(option?.value ?? '').startsWith(input);

// Pending extra time a not-joined student WILL park on first load — the
// monitor's not-joined rows show it as "⏳ pending". Mirrors the
// InitRealtimeExperience park EXACTLY: gate on extra_time/pending_extra_time
// never set (`== null`; a reset writes 0s which must not re-derive), resolve
// through the same monitor ?? userList || delivery precedence. Gating on
// `accommodations == null` instead (pre-R2-J11) made any monitor-authored
// toggle save hide a still-effective profile grant from the monitor.
export const deriveNotJoinedPendingExtraTimeMs = (
  placeholder: {
    extra_time?: number | null;
    pending_extra_time?: number | null;
    accommodations?: Record<string, any> | null;
  },
  userCustomFieldsAccommodations: Record<string, any> | undefined,
  experienceSettings: { duration?: number } | undefined
): number | undefined => {
  if (placeholder.extra_time != null || placeholder.pending_extra_time != null) return undefined;
  const resolved = resolveAccommodations(
    userCustomFieldsAccommodations,
    experienceSettings,
    placeholder.accommodations
  );
  const pendingMs = getExtraTimeMs(resolved, experienceSettings?.duration ?? 0);
  return pendingMs > 0 ? pendingMs : undefined;
};

// The BE `.compact`s the reset's `started_at: nil` away, so the doc keeps the
// previous attempt's stamp; `reset_submission_at` survives and dates the reset.
// Returns MonitorCountdown's `null` not-started sentinel. Both are epoch ms.
export const resolveStartedAtSinceReset = (item: StudentRealtimeData): number | null => {
  const startedAt = item?.started_at ?? null;
  const resetAt = item?.reset_submission_at ?? 0;
  if (!resetAt) return startedAt;
  return startedAt != null && startedAt > resetAt ? startedAt : null;
};

// The reset doesn't clear `unpaused_offset`, so a pre-reset value would inflate
// the fresh attempt's target.
export const resolveUnpausedOffsetSinceReset = (item: StudentRealtimeData): number =>
  resolveStartedAtSinceReset(item) === null && (item?.reset_submission_at ?? 0) > 0
    ? 0
    : item?.unpaused_offset ?? 0;

// Compute extra_time_offset for the live timer when adding extra time mid-test.
//
// CountDownTimer formula:
//   targetTime = started_at + duration + extra_time + extra_time_offset + unpaused_offset
//
// IMPORTANT: this MUST use the OLD `item.extra_time` (current realtime value),
// NOT the new value being saved. Using the new value produces a negative offset
// and breaks the timer. See APL-4245 PR review threads #1/#2 for the full proof.
//
// - endTest + is_time_up: original timer already expired. We pay back the
//   non-pause dead time (now - oldTotal - unpaused_offset) so the new
//   extra_time starts counting from "now". `unpaused_offset` is subtracted
//   because pause windows are already accounted for separately in the timer
//   formula; including them here would double-count any pause that happened
//   after the timer expired (e.g. teacher pauses on time-up, waits 10 min,
//   unpauses, then adds +5 min — without this subtraction the student would
//   see 15 min remaining instead of 5).
// - endTest, not yet expired: keep whatever offset is already there.
// - startTest: keep existing offset (timer hasn't started counting against the
//   student yet, so no dead-time correction needed).
// - any other view (e.g. submittedTest): no offset.
export const computeExtraTimeOffset = (
  item: StudentRealtimeData,
  experienceDuration: number
): number => {
  let result = 0;
  if (item?.currentView === 'endTest') {
    const totalTestTime =
      (item.started_at ?? 0) + convertMs(experienceDuration) + (item.extra_time ?? 0);
    const unpausedOffset = item.unpaused_offset ?? 0;
    result = item.is_time_up
      ? Date.now() - totalTestTime - unpausedOffset
      : item.extra_time_offset || 0;
  } else if (item?.currentView === 'startTest') {
    result = item.extra_time_offset || 0;
  }
  return result;
};
